/**
 * Utility to convert JSON export data to Excel (XLSX) format.
 * Requires: npm install xlsx
 */

import {
  ExportColumnKey,
  FoodLog,
  PatientSummary,
  getExportColumnOptions
} from 'services/exportService';

const splitPortion = (portion: unknown) => {
  if (portion === null || portion === undefined) {
    return { value: '', unit: '' };
  }

  if (typeof portion === 'number') {
    return { value: portion, unit: '' };
  }

  const text = String(portion).trim();
  const match = text.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*(.*)$/);

  if (!match) {
    return { value: text, unit: '' };
  }

  const numericValue = Number(match[1]);
  return {
    value: Number.isNaN(numericValue) ? match[1] : numericValue,
    unit: match[2].trim()
  };
};

const getGoalType = (
  log: FoodLog,
  summaryByPatient: Map<number, PatientSummary>
) => {
  if (log.goalType) return log.goalType;
  return summaryByPatient.get(log.patientId)?.activeGoalTypes?.join(', ') || '';
};

const getLoggedDateKey = (log: FoodLog) => {
  const [day, month, year] = log.date.split('/');
  return `${year}-${month}-${day}`;
};

const getInclusiveDayCount = (dateFrom: string, dateTo: string) => {
  const start = new Date(`${dateFrom}T00:00:00Z`).getTime();
  const end = new Date(`${dateTo}T00:00:00Z`).getTime();
  return Math.max(1, Math.floor((end - start) / 86400000) + 1);
};

export const convertToExcel = async (
  foodLogs: FoodLog[],
  patientSummaries: PatientSummary[],
  exportType: string,
  dateFrom: string,
  dateTo: string,
  selectedColumnKeys?: ExportColumnKey[]
) => {
  // Dynamic import for xlsx to avoid dependency if not needed
  const XLSX = await import('xlsx');

  const summaryByPatient = new Map(
    patientSummaries.map(summary => [summary.patientId, summary])
  );
  const isClinical = exportType === 'clinical';
  const availableColumns = getExportColumnOptions(
    isClinical ? 'clinical' : 'research'
  );
  const selectedKeys = new Set(
    selectedColumnKeys || availableColumns.map(column => column.key)
  );
  const selectedColumns = availableColumns.filter(column =>
    selectedKeys.has(column.key)
  );

  const foodLogHeaders = selectedColumns.map(column => column.label);

  const foodLogRows = foodLogs.map(log => {
    const portion = splitPortion(log.portionValue ?? log.portion);
    const values: Record<ExportColumnKey, unknown> = {
      patientId: log.patientId,
      patientName: log.patientName || '',
      hospitalFileId: log.hospitalFileId || '',
      hospitalSite: log.hospitalSite || '',
      dietitianId: log.dietitianId || '',
      date: log.date,
      time: log.time,
      brand: log.brandName || log.brand || '',
      product: log.productName || log.product || '',
      portionValue: portion.value,
      portionUnit: log.portionUnit || portion.unit,
      calories: log.calories,
      protein: log.protein,
      carbs: log.carbs,
      fat: log.fat,
      goalType: getGoalType(log, summaryByPatient)
    };
    return selectedColumns.map(column => values[column.key]);
  });

  const foodLogsSheet = XLSX.utils.aoa_to_sheet([
    foodLogHeaders,
    ...foodLogRows
  ]);

  const summaryColumnLabels: Partial<Record<ExportColumnKey, string>> = {
    patientId: 'MLAS Patient ID',
    patientName: 'Patient Name',
    hospitalFileId: 'Hospital File ID',
    hospitalSite: 'Hospital/Site',
    dietitianId: 'Dietitian ID',
    goalType: 'Goal Type',
    calories: 'Total Calories',
    protein: 'Total Protein',
    carbs: 'Total Carbs',
    fat: 'Total Fat'
  };
  const summaryColumns = selectedColumns.filter(
    column => summaryColumnLabels[column.key]
  );
  const patientSummaryHeaders = [
    ...summaryColumns.map(column => summaryColumnLabels[column.key]),
    'Average Daily Calories',
    'Percentage of Days Logged',
    'Weight Change'
  ];
  const rangeDayCount = getInclusiveDayCount(dateFrom, dateTo);
  const patientSummaryRows = patientSummaries.map(summary => {
    const patientLogs = foodLogs.filter(
      log => log.patientId === summary.patientId
    );
    const loggedDates = new Set(patientLogs.map(getLoggedDateKey));
    const calories = patientLogs.reduce(
      (total, log) => total + (Number(log.calories) || 0),
      0
    );

    const summaryValues: Partial<Record<ExportColumnKey, unknown>> = {
      patientId: summary.patientId,
      patientName: summary.patientName || '',
      hospitalFileId: summary.hospitalFileId || '',
      hospitalSite: summary.hospitalSite || '',
      dietitianId: summary.dietitianId || '',
      goalType: summary.activeGoalTypes?.join(', ') || '',
      calories: summary.totalCalories,
      protein: summary.totalProtein,
      carbs: summary.totalCarbs,
      fat: summary.totalFat
    };
    return [
      ...summaryColumns.map(column => summaryValues[column.key]),
      summary.averageDailyCalories ??
        (loggedDates.size
          ? Number((calories / loggedDates.size).toFixed(2))
          : 0),
      summary.percentageOfDaysLogged ??
        Number(((loggedDates.size / rangeDayCount) * 100).toFixed(2)),
      summary.weightChange ?? ''
    ];
  });
  const patientSummarySheet = XLSX.utils.aoa_to_sheet([
    patientSummaryHeaders,
    ...patientSummaryRows
  ]);

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, foodLogsSheet, 'Food Logs');
  XLSX.utils.book_append_sheet(
    workbook,
    patientSummarySheet,
    'Patient Summary'
  );

  // Generate filename with date range
  const fileName = `Food-Data-Export-${exportType}-${dateFrom}-${dateTo}.xlsx`;

  // Write file
  XLSX.writeFile(workbook, fileName);
};
